import { VisualizationData } from '../components/csv/VisualizationRenderer';

interface ParsedSection {
  type: 'text' | 'visualization';
  content: string | VisualizationData;
}

interface ParsedResponse {
  sections: ParsedSection[];
}

/**
 * Helper function to fix incomplete JSON by adding missing brackets and braces
 * @param jsonStr The potentially incomplete JSON string
 * @returns A fixed JSON string with balanced brackets and braces
 */
const fixIncompleteJson = (jsonStr: string): string => {
  let fixed = jsonStr.trim();
  
  // Count opening and closing braces/brackets
  const openBraces = (fixed.match(/\{/g) || []).length;
  const closeBraces = (fixed.match(/\}/g) || []).length;
  const openBrackets = (fixed.match(/\[/g) || []).length;
  const closeBrackets = (fixed.match(/\]/g) || []).length;
  
  
  
  // Check for specific histogram pattern with incomplete datasets array
  const histogramWithIncompleteDatasets = fixed.includes('"type"') && 
                                         fixed.includes('"histogram"') && 
                                         fixed.includes('"datasets"') && 
                                         openBrackets > closeBrackets;
  
  if (histogramWithIncompleteDatasets) {
    
    
    // Find the last complete property
    const lastPropertyMatch = fixed.match(/,\s*"([^"]+)"\s*:/g);
    if (lastPropertyMatch) {
      const lastProperty = lastPropertyMatch[lastPropertyMatch.length - 1].trim();

    }
  }
  
  // Add missing closing brackets
  if (openBrackets > closeBrackets) {

    fixed += ']'.repeat(openBrackets - closeBrackets);
  }
  
  // Add missing closing braces
  if (openBraces > closeBraces) {

    fixed += '}'.repeat(openBraces - closeBraces);
  }
  
  return fixed;
};

/**
 * Parses the AI response text to extract visualization data
 * @param responseText The raw text response from the AI
 * @returns Object containing the original text content and visualization data
 */
export const parseAIResponse = (responseText: string): ParsedResponse => {
  const sections: ParsedSection[] = [];
  if (!responseText) {
    return { sections };
  }

  
  // Regex to match visualization code blocks (more flexible)
  const visualizationBlockRegex = /```(?:chart|visualization)\s*([\s\S]*?)```/gi;
  let lastIndex = 0;
  let match;
  let visualizationCount = 0;

  while ((match = visualizationBlockRegex.exec(responseText)) !== null) {
    // Text before this visualization block
    if (match.index > lastIndex) {
      const textPart = responseText.substring(lastIndex, match.index);
      if (textPart.trim()) {
        sections.push({ type: 'text', content: textPart });
      }
    }
    
    // Visualization block
    const jsonContent = match[1].trim();
    if (jsonContent) {
      try {
        // FIRST: Try to parse the JSON as-is without any modifications
        let vizData;
        try {
          vizData = JSON.parse(jsonContent);
        } catch (initialError) {
          
          // SECOND: Only apply fixes if the initial parsing failed
          let fixedJsonContent = jsonContent;
          
          // Basic cleanup - only if needed
          fixedJsonContent = fixedJsonContent
            .replace(/(\r\n|\n|\r)/gm, ' ') // Remove line breaks
            .replace(/\s+/g, ' ')  // Normalize whitespace
            .trim();
          
          // Fix single quotes to double quotes (common issue)
          fixedJsonContent = fixedJsonContent.replace(/'/g, '"');
          
          // Remove trailing commas
          fixedJsonContent = fixedJsonContent
            .replace(/,\s*}/g, '}')  // Remove trailing commas in objects
            .replace(/,\s*\]/g, ']'); // Remove trailing commas in arrays
          
          // Only add quotes around unquoted property names if they exist
          fixedJsonContent = fixedJsonContent.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
          
          // Check for missing braces only if obviously needed
          const openBraces = (fixedJsonContent.match(/\{/g) || []).length;
          const closeBraces = (fixedJsonContent.match(/\}/g) || []).length;
          
          if (openBraces > closeBraces) {
            fixedJsonContent = fixIncompleteJson(fixedJsonContent);
          } else if (!fixedJsonContent.startsWith('{') && !fixedJsonContent.startsWith('[')) {
            fixedJsonContent = '{' + fixedJsonContent + '}';
          }
          
          console.log(`Attempting first fix: ${fixedJsonContent.substring(0, 100)}...`);
          
          try {
            vizData = JSON.parse(fixedJsonContent);
            console.log('Successfully parsed JSON with basic fixes');
          } catch (secondError) {
            console.warn('Basic fixes failed, trying aggressive fixes:', secondError);
            
            // THIRD: More aggressive fixes only as last resort
            fixedJsonContent = fixedJsonContent
              // Ensure string values in arrays have quotes
              .replace(/\[([^"\]\[]*?)\]/g, (match, content) => {
                const items = content.split(',').map((item: string) => {
                  item = item.trim();
                  // If it's not a number, boolean, or already quoted string, add quotes
                  if (!/^(true|false|null|\d+|\d+\.\d+|".*")$/.test(item) && item !== '') {
                    return `"${item}"`;
                  }
                  return item;
                });
                return '[' + items.join(',') + ']';
              })
              // Fix property values that should be strings
              .replace(/:\s*([^",\}\]\d][^,\}\]]*?)([,\}\]])/g, (match, value, end) => {
                value = value.trim();
                // If not a recognized non-string value, quote it
                if (!/^(true|false|null|\[.*\]|\{.*\})$/.test(value)) {
                  return `: "${value}"${end}`;
                }
                return `: ${value}${end}`;
              });
            
            console.log('Attempting aggressive fix:', fixedJsonContent.substring(0, 100) + '...');
            
            try {
              vizData = JSON.parse(fixedJsonContent);
            } catch (thirdError) {
              // FOURTH: Special handling for histograms as fallback
              if (fixedJsonContent.includes('"type"') && fixedJsonContent.includes('"histogram"')) {
                
                const histogramTemplate: VisualizationData = {
                  type: "histogram",
                  title: "Histogram",
                  description: "Histogram data",
                  labels: [],
                  datasets: [{
                    label: "Frequency",
                    data: []
                  }],
                  bins: 10,
                  xAxisLabel: "Value",
                  yAxisLabel: "Frequency"
                };
                
                // Try to extract the datasets array
                const datasetsMatch = fixedJsonContent.match(/"datasets"\s*:\s*\[\s*\{\s*"label"\s*:\s*"[^"]+"\s*,\s*"data"\s*:\s*\[([\d\.,\s]+)/);
                if (datasetsMatch && datasetsMatch[1]) {
                  const dataValues = datasetsMatch[1].match(/\d+(\.\d+)?/g);
                  if (dataValues && dataValues.length > 0) {
                    histogramTemplate.datasets[0].data = dataValues.map(v => parseFloat(v)) as number[];
                    
                    // Extract other properties if available
                    const titleMatch = fixedJsonContent.match(/"title"\s*:\s*"([^"]+)"/);
                    if (titleMatch) histogramTemplate.title = titleMatch[1];
                    
                    const descMatch = fixedJsonContent.match(/"description"\s*:\s*"([^"]+)"/);
                    if (descMatch) histogramTemplate.description = descMatch[1];
                    
                    const binsMatch = fixedJsonContent.match(/"bins"\s*:\s*(\d+)/);
                    if (binsMatch) histogramTemplate.bins = parseInt(binsMatch[1], 10);
                    
                    const xAxisMatch = fixedJsonContent.match(/"xAxisLabel"\s*:\s*"([^"]+)"/);
                    if (xAxisMatch) histogramTemplate.xAxisLabel = xAxisMatch[1];
                    
                    const yAxisMatch = fixedJsonContent.match(/"yAxisLabel"\s*:\s*"([^"]+)"/);
                    if (yAxisMatch) histogramTemplate.yAxisLabel = yAxisMatch[1];
                    
                    vizData = histogramTemplate;
                    console.log('Successfully reconstructed histogram data');
                  }
                }
              }
              
              if (!vizData) {
                throw thirdError; // Re-throw if we couldn't fix it
              }
            }
          }
        }
        
        // Validate and process the visualization data
        if (vizData && vizData.type && vizData.datasets) {
          
          // Ensure the visualization data has all required fields
          vizData.type = vizData.type.toLowerCase();
          vizData.title = vizData.title || 'Chart';
          vizData.description = vizData.description || '';
          
          // Always ensure labels array exists
          vizData.labels = vizData.labels || [];
          
          // Handle labels array based on chart type
          if (vizData.type === 'scatter' || vizData.type === 'histogram') {
            // For scatter plots and histograms, create labels if missing or empty
            if (!vizData.labels || vizData.labels.length === 0) {
              const firstDataset = vizData.datasets[0];
              if (firstDataset && firstDataset.data && Array.isArray(firstDataset.data)) {
                // For histograms, we don't need to generate labels as they'll be created during processing
                if (vizData.type !== 'histogram') {
                  // Generate labels for each data point
                  vizData.labels = firstDataset.data.map((point: any, index: number) => {
                    if (typeof point === 'object' && 'x' in point && 'y' in point) {
                      return `Point ${index + 1}`;
                    }
                    return `Data ${index + 1}`;
                  });
                }
              } else {
                vizData.labels = ['Data Point'];
              }
            }
          }
          
          // Ensure each dataset has required properties
          vizData.datasets = vizData.datasets.map((dataset: any) => {
            return {
              label: dataset.label || 'Data',
              data: dataset.data || [],
              backgroundColor: dataset.backgroundColor || undefined,
              borderColor: dataset.borderColor || undefined
            };
          });
          
          visualizationCount++;
          sections.push({ type: 'visualization', content: vizData });
        } else {
          console.warn('Visualization data missing required fields:', vizData);
          
          // Try to fix the data structure if possible
          if (vizData && vizData.type) {
            const fixedVizData: VisualizationData = {
              type: (vizData.type || 'bar').toLowerCase() as any,
              title: vizData.title || 'Chart',
              description: vizData.description || '',
              labels: vizData.labels || ['No Data'],
              datasets: [{
                label: 'Data',
                data: [0],
                backgroundColor: 'rgba(66, 133, 244, 0.7)'
              }]
            };
            
            // If we have datasets but they're not properly formatted
            if (vizData.datasets && Array.isArray(vizData.datasets)) {
              fixedVizData.datasets = vizData.datasets.map((ds: any) => ({
                label: ds.label || 'Data',
                data: Array.isArray(ds.data) ? ds.data : [0],
                backgroundColor: ds.backgroundColor || 'rgba(66, 133, 244, 0.7)',
                borderColor: ds.borderColor
              }));
            }
            
            sections.push({ type: 'visualization', content: fixedVizData });
            visualizationCount++;
          } else {
            // Add as text if we couldn't fix it
            sections.push({ 
              type: 'text', 
              content: `\`\`\`json\n${jsonContent}\n\`\`\`\n\n[Error: Could not parse visualization data]` 
            });
          }
        }
      } catch (e) {
        console.error('Error parsing visualization JSON:', e);
        console.error('Problematic JSON content:', jsonContent);
        // Add as text to ensure content isn't lost
        sections.push({ 
          type: 'text', 
          content: `\`\`\`json\n${jsonContent}\n\`\`\`\n\n[Error: Could not parse visualization data]` 
        });
      }
    }
    
    lastIndex = visualizationBlockRegex.lastIndex;
  }
  
  // Any remaining text after the last visualization
  if (lastIndex < responseText.length) {
    const textPart = responseText.substring(lastIndex);
    if (textPart.trim()) {
      sections.push({ type: 'text', content: textPart });
    }
  }
  
  // Fallback: Look for JSON blocks that might be visualizations but not properly wrapped
  const jsonBlockRegex = /```(?:json)?\s*(\{[\s\S]*?\})\s*```/gi;
  let jsonMatch;
  
  while ((jsonMatch = jsonBlockRegex.exec(responseText)) !== null) {
    const jsonContent = jsonMatch[1].trim();
    
    // Check if this JSON looks like a chart (contains type, title, data, etc.)
    if (jsonContent.includes('"type"') && 
        (jsonContent.includes('"bar"') || jsonContent.includes('"line"') || 
         jsonContent.includes('"pie"') || jsonContent.includes('"chart"')) &&
        jsonContent.includes('"data"')) {
      
      try {
        const vizData = JSON.parse(jsonContent);
        if (vizData.type && (vizData.datasets || vizData.data)) {
          sections.push({ type: 'visualization', content: vizData });
          visualizationCount++;
        }
      } catch (error) {
        // Silently continue if JSON parsing fails
      }
    }
  }
  
  return { sections };
};

/**
 * Converts markdown text to HTML for rendering
 * @param markdown The markdown text to convert
 * @returns HTML string
 */
export const convertMarkdownToHTML = (markdown: string): string => {
  if (!markdown) return '';
  
  // Clean up markdown first (normalize line endings, etc.)
  const cleanedMarkdown = markdown
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');
  
  // Pre-process to detect tables that might not be properly formatted
  let preprocessedMarkdown = cleanedMarkdown;
  
  // Helper function to insert a separator row
  const insertSeparatorRow = (lines: string[], headerIndex: number) => {
    // Extract columns from the header row
    const headerRow = lines[headerIndex];
    const columnCount = headerRow.match(/<!-- columns: (\d+) -->/)?.[1];
    
    if (columnCount) {
      // Create a separator row with the correct number of columns
      const numColumns = parseInt(columnCount, 10);
      const separatorRow = '|' + Array(numColumns).fill(' --- |').join('');
      
      // Insert the separator row after the header
      lines.splice(headerIndex + 1, 0, separatorRow);
    } else {
      // Fallback if we can't determine column count
      const columns = headerRow.split('|').filter(col => col.trim() !== '');
      
      // Create a separator row
      const separatorRow = '| ' + columns.map(() => '-------- ').join('|') + '|';
      
      // Insert the separator row after the header
      lines.splice(headerIndex + 1, 0, separatorRow);
    }
  };
  
  // Find potential table rows (lines that have multiple | characters)
  const tableRowRegex = /^([^|\n]*\|[^|\n]*\|[^|\n]*)+$/gm;
  
  // Replace all pipe characters in code blocks to avoid being treated as tables
  preprocessedMarkdown = preprocessedMarkdown.replace(/```[\s\S]*?```/g, (match) => {
    return match.replace(/\|/g, '&#124;');
  });
  
  // Replace all pipe characters in inline code to avoid being treated as tables
  preprocessedMarkdown = preprocessedMarkdown.replace(/`([^`]+)`/g, (match, content) => {
    return '`' + content.replace(/\|/g, '&#124;') + '`';
  });
  
  // Collect all potential table rows
  const tableRows: RegExpExecArray[] = [];
  let tableRowMatch: RegExpExecArray | null;
  while ((tableRowMatch = tableRowRegex.exec(preprocessedMarkdown)) !== null) {
    tableRows.push(tableRowMatch);
  }
  
  // Group consecutive table rows into tables and add proper formatting if missing
  if (tableRows.length > 0) {
    let currentTableStart = -1;
    let inTable = false;
    let hasSeparatorRow = false;
    
    // Process the markdown line by line
    const lines = preprocessedMarkdown.split('\n');
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip lines that don't look like table rows
      if (!line || line.startsWith('#') || line.startsWith('>') || line.startsWith('```')) {
        if (inTable) {
          // End of a table
          inTable = false;
          
          // If we didn't find a separator row, insert one after the header
          if (!hasSeparatorRow && currentTableStart >= 0 && i - currentTableStart > 1) {
            insertSeparatorRow(lines, currentTableStart);
            i++; // Adjust index since we inserted a row
          }
        }
        continue;
      }
      
      // Check if this line is a potential table row (has multiple | characters)
      if (line.match(/^([^|\n]*\|[^|\n]*\|[^|\n]*)+$/)) {
        if (!inTable) {
          // Start of a new table
          inTable = true;
          currentTableStart = i;
          hasSeparatorRow = false;
          
          // Make sure first line starts with |
          if (!line.startsWith('|')) {
            lines[i] = '| ' + line;
          }
          
          // Make sure first line ends with |
          if (!line.endsWith('|')) {
            lines[i] = lines[i] + ' |';
          }
          
          // Count number of columns (by counting pipe characters)
          const columnCount = (lines[i].match(/\|/g) || []).length - 1;
          
          // Store the column count for later validation
          lines[i] = lines[i] + ` <!-- columns: ${columnCount} -->`;
        } else {
          // Continuing a table
          
          // Check if this is a separator row (contains --- or :--:)
          if (line.includes('---') || line.includes(':--') || line.includes('--:')) {
            hasSeparatorRow = true;
            
            // Make sure separator row starts with |
            if (!line.startsWith('|')) {
              lines[i] = '| ' + line;
            }
            
            // Make sure separator row ends with |
            if (!line.endsWith('|')) {
              lines[i] = lines[i] + ' |';
            }
            
            // Ensure every cell in the separator row has at least ---
            const cells = lines[i].split('|').map(cell => cell.trim());
            const fixedCells = cells.map(cell => {
              if (cell === '' || cell === '-' || cell === '--') return '---';
              if (!cell.includes('-')) return '---';
              return cell;
            });
            lines[i] = fixedCells.join(' | ');
            
            // Get column count from header row
            const headerComment = lines[currentTableStart].match(/<!-- columns: (\d+) -->/);
            if (headerComment && headerComment[1]) {
              const expectedColumns = parseInt(headerComment[1], 10);
              const actualColumns = (lines[i].match(/\|/g) || []).length - 1;
              
              // If separator row has wrong number of columns, fix it
              if (actualColumns !== expectedColumns) {
                lines[i] = '|' + Array(expectedColumns).fill(' --- |').join('');
              }
            }
          } else {
            // It's a data row
            
            // Make sure data row starts with |
            if (!line.startsWith('|')) {
              lines[i] = '| ' + line;
            }
            
            // Make sure data row ends with |
            if (!line.endsWith('|')) {
              lines[i] = lines[i] + ' |';
            }
            
            // Get column count from header row
            const headerComment = lines[currentTableStart].match(/<!-- columns: (\d+) -->/);
            if (headerComment && headerComment[1]) {
              const expectedColumns = parseInt(headerComment[1], 10);
              const actualColumns = (lines[i].match(/\|/g) || []).length - 1;
              
              // If data row has wrong number of columns, fix it
              if (actualColumns < expectedColumns) {
                // Add missing cells
                const missingCells = expectedColumns - actualColumns;
                lines[i] = lines[i].replace(/\|\s*$/, Array(missingCells + 1).fill('| ').join(''));
              } else if (actualColumns > expectedColumns) {
                // Remove extra cells
                const parts = lines[i].split('|');
                lines[i] = parts.slice(0, expectedColumns + 2).join('|');
              }
            }
          }
        }
      } else if (inTable) {
        // End of a table
        inTable = false;
        
        // If we didn't find a separator row, insert one after the header
        if (!hasSeparatorRow && currentTableStart >= 0 && i - currentTableStart > 1) {
          insertSeparatorRow(lines, currentTableStart);
          i++; // Adjust index since we inserted a row
        }
      }
    }
    
    // If the last row was part of a table
    if (inTable && !hasSeparatorRow && currentTableStart >= 0) {
      insertSeparatorRow(lines, currentTableStart);
    }
    
    // Cleanup the column count comments
    for (let i = 0; i < lines.length; i++) {
      lines[i] = lines[i].replace(/<!-- columns: \d+ -->/, '');
    }
    
    // Rejoin the lines
    preprocessedMarkdown = lines.join('\n');
  }
  
  // Process markdown into HTML
  let html = preprocessedMarkdown
    // Headings
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    
    // Bold and italic
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    
    // Paragraphs
    .replace(/^\s*(\n)?(.+)/gm, function(match) {
      const trimmedMatch = match.trim();
      if (
        /^<(\/)?(h[1-6]|ul|ol|li|blockquote|pre|img|table|thead|tbody|tr|th|td)/.test(trimmedMatch) ||
        /^---/.test(trimmedMatch) ||
        /^&gt;/.test(trimmedMatch)
      ) {
        return match;
      }
      
      return `<p>${trimmedMatch}</p>`;
    })
    
    // Lists - removed conflicting patterns, will be handled in the main processing section
    // (This section is now handled by the improved list processing below)
    
    // Tables - improved with better regex
    .replace(/^\s*\|(.+)\|\s*$/gm, function(match: string, content: string) {
      // Row content
      const columns = content.split('|').map((cell: string) => cell.trim());
      // Don't apply to rows that are just separator rows (with dashes)
      if (columns.every(col => /^[-:]+$/.test(col))) {
        return match;
      }
      return `<tr>${columns.map((cell: string) => `<td>${cell}</td>`).join('')}</tr>`;
    })
    .replace(/^\s*\|([-:]+[-| :]*)\|\s*$/gm, function(match: string, content: string) {
      // Header/divider row with alignment information
      const columns = content.split('|').map((cell: string) => cell.trim());
      
      // Start the table and add thead
      return `<table class="markdown-table"><thead><tr>${columns.map((cell: string) => {
        let align = 'left';
        if (cell.startsWith(':') && cell.endsWith(':')) align = 'center';
        else if (cell.endsWith(':')) align = 'right';
        return `<th style="text-align: ${align}"></th>`;
      }).join('')}</tr></thead><tbody>`;
    })
    
    // Fix table structure
    .replace(/<\/tr>\s*<table class="markdown-table">/g, '</tr></tbody></table><table class="markdown-table">')
    .replace(/<tr>(.+?)<\/tr>\s*<thead>/g, function(match: string, content: string) {
      // First row should be headers 
      return `<thead><tr>${content.replace(/<td>/g, '<th>').replace(/<\/td>/g, '</th>')}</tr></thead><tbody>`;
    })
    .replace(/<\/tr>\s*<\/tbody>/g, '</tr>')
    .replace(/<\/tr>\s*$/g, '</tr></tbody></table>')
    
    // Remove visualization code blocks to prevent them from being displayed as code
    .replace(/```visualization[\s\S]*?```/g, '')
    
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre style="background-color: #f8f8f8; padding: 18px; border-radius: 6px; overflow-x: auto; margin-bottom: 24px; border-left: 4px solid #2a4065; text-align: left !important;"><code style="font-family: monospace; color: #333;">$1</code></pre>')
    
    // Inline code
    .replace(/`([^`]+)`/g, '<code style="background-color: #f5f5f5; padding: 3px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9em; text-align: left !important; color: #d32f2f;">$1</code>')
    
    // Quotes
    .replace(/^>\s+(.*?)$/gim, '<blockquote>$1</blockquote>')
    
    // Line Breaks - ensure all newlines are preserved
    .replace(/\n/g, '<br>');
  
  // Process lists - wrap all list items in ul tags
  let processedHtml = '';
  let insideList = false;
  
  // Split by list items
  const htmlParts = html.split('<li');
  
  htmlParts.forEach((part, i) => {
    if (i === 0) {
      // First part before any list item
      processedHtml += part;
    } else {
      if (!insideList) {
        if (part.includes('</ol>')) {
          processedHtml += '<ol><li' + part;
          insideList = true;
        } else if (part.includes('</ul>')) {
          processedHtml += '<ul><li' + part;
          insideList = true;
        } else {
          processedHtml += '<ul><li' + part;
          insideList = true;
        }
      } else {
        // Already inside a list
        processedHtml += '<li' + part;
        
        if ((part.includes('</ul>') && !part.includes('<ul>')) ||
            (part.includes('</ol>') && !part.includes('<ol>'))) {
          insideList = false;
        }
      }
    }
  });
  
  // Clean up any broken table structures
  processedHtml = processedHtml.replace(/<table class="markdown-table">(?![\s\S]*?<\/table>)/g, '<table class="markdown-table"><tbody><tr><td>');
  processedHtml = processedHtml.replace(/<thead>(?![\s\S]*?<\/thead>)/g, '<thead><tr><th></th></tr></thead>');
  processedHtml = processedHtml.replace(/<tbody>(?![\s\S]*?<\/tbody>)/g, '<tbody><tr><td></td></tr></tbody>');
  
  // Final cleanup for any unclosed tags
  if (processedHtml.includes('<table') && !processedHtml.includes('</table>')) {
    processedHtml += '</tbody></table>';
  }
  
  return processedHtml;
};

/**
 * Format numbers and percentages in text with appropriate styling
 * @param text The text to format
 * @returns Formatted text with styled numbers
 */
export const formatTextWithNumbers = (text: string): string => {
  // Format percentages like "XX.X%" or "XX%" with proper styling
  const percentRegex = /(\d+(\.\d+)?\s*%)/g;
  
  // Format large numbers with commas, but avoid formatting years and dates
  const standaloneNumberRegex = /(?<!\d-)(?<!\d\.)(?<!\d\/)\b(\d{4,}(\.\d+)?)\b(?!-\d)(?!\.\d)(?!\/\d)/g;
  
  // Format currency values like "$XX.XX" or "$XX,XXX.XX"
  const currencyRegex = /(\$\s*\d{1,3}(,\d{3})*(\.\d+)?)/g;
  
  // Apply formatting in order
  let formattedText = text
    .replace(percentRegex, '<span class="percentage-value">$1</span>')
    .replace(standaloneNumberRegex, (match) => {
      // Don't format if it's already part of a currency value
      if (text.includes('$' + match) || text.includes('$ ' + match)) return match;
      
      // Don't format if it's a 4-digit year (1900-2099)
      const num = parseFloat(match);
      if (num >= 1900 && num <= 2099 && match.length === 4) {
        return match;
      }
      
      // Don't format if it looks like a year range
      if (text.match(new RegExp(`${match}-\\d{4}`)) || text.match(new RegExp(`\\d{4}-${match}`))) {
        return match;
      }
      
      // Don't format if it's in a context that suggests it's a year
      const yearContextRegex = new RegExp(`(Q[1-4]\\s+${match}|${match}\\s+Quarterly|${match}\\s+Report|Report\\s+${match}|${match}\\s+Business)`, 'i');
      if (yearContextRegex.test(text)) {
        return match;
      }
      
      // Format with commas for thousands
      return '<span class="number-value">' + num.toLocaleString() + '</span>';
    })
    .replace(currencyRegex, '<span class="currency-value">$1</span>');
  
  return formattedText;
};

/**
 * Hides calculation sections by wrapping them in collapsible details elements
 * @param markdown The markdown text to process
 * @param shouldHide Whether to hide the sections (true) or show them (false)
 * @returns Markdown with calculation sections hidden or shown
 */
const hideCalculationSections = (markdown: string, shouldHide: boolean = true): string => {
  if (!markdown) return '';
  
  // Define sections to hide
  const sectionsToHide = [
    'RULE CALCULATIONS',
    'RULE CALCULATIONS DOUBLE CHECK',
    'DATA VALIDATION',
    'CALCULATION VERIFICATION',
    'RANKING VERIFICATION',
    'CALCULATION DETAILS',
    'DETAILED RULE EVALUATION',
    'VERIFICATION CHECK',
    'FINAL VERIFIED CALCULATIONS'
  ];
  
  let processed = markdown;
  
  // If shouldHide is false, just return the original markdown
  if (!shouldHide) {
    return processed;
  }
  
  for (const sectionName of sectionsToHide) {
    // Create regex to match the section and all content until the next ## header or end of string
    const sectionRegex = new RegExp(
      `## ${sectionName}\\s*\\n([\\s\\S]*?)(?=\\n## |$)`,
      'gi'
    );
    
    // Completely remove the sections instead of collapsing them
    processed = processed.replace(sectionRegex, '');
  }
  
  return processed;
};

/**
 * Extract final verified scores from KPI analysis and create a clean table
 * @param markdown The markdown text containing the AI analysis
 * @returns Object with extracted scores and dealer info
 */
const extractFinalVerifiedScores = (markdown: string): { dealers: Array<{dealer: string, year: string, month: string, score: number}>, hasScores: boolean } => {
  if (!markdown) return { dealers: [], hasScores: false };
  
  const dealers: Array<{dealer: string, year: string, month: string, score: number}> = [];
  
  console.log("Extracting scores from markdown:", markdown.substring(0, 500) + "...");
  
  // Look for various patterns of corrected/final scores
  const correctedScorePatterns = [
    /\*\*Final Verified Score\*\*:\s*(\d+)/gi,
    /\*\*Corrected Final Score:\s*(\d+)\*\*/gi,
    /Final Verified Score[^:]*:\s*(\d+)/gi,
    /\*\*Final Score:\s*(\d+)\*\*/gi,
    /correct score should be (\d+)/gi,
    /Final score:\s*100\s*-\s*\d+\s*=\s*(\d+)/gi
  ];
  
  let finalScore: number | null = null;
  let allFoundScores: Array<{score: number, index: number, pattern: string, fullMatch: string}> = [];
  
  // Collect ALL matches from ALL patterns to find the absolute last one
  for (const pattern of correctedScorePatterns) {
    const matches = markdown.match(pattern);
    if (matches) {
      matches.forEach(match => {
        const scoreMatch = match.match(/(\d+)/);
        if (scoreMatch) {
          // Find the index of this match in the original text
          const index = markdown.indexOf(match);
          allFoundScores.push({
            score: parseInt(scoreMatch[1]),
            index: index,
            pattern: pattern.source,
            fullMatch: match
          });
        }
      });
    }
  }
  
  // Sort by index (position in text) and get the LAST one
  if (allFoundScores.length > 0) {
    allFoundScores.sort((a, b) => a.index - b.index);
    const lastScore = allFoundScores[allFoundScores.length - 1];
    finalScore = lastScore.score;
    console.log("Found", allFoundScores.length, "scores:", allFoundScores.map(s => s.score));
    console.log("Using LAST final score:", finalScore, "from pattern:", lastScore.pattern);
    console.log("Full match:", lastScore.fullMatch);
  }
  
  if (finalScore) {
    // Extract dealer information from the original table or context
    let dealerName = 'Unknown Dealer';
    let year = '2024';
    let month = '1';
    
    // Look for dealer name in various formats (generic patterns, not hardcoded names)
    const dealerPatterns = [
      // Table format: | DealerName | Year | Month | Score |
      /\|\s*([^|]+?)\s*\|\s*(\d{4})\s*\|\s*(\d+)\s*\|\s*\d+\s*\|/gi,
      // Record format: **Record: DealerName - Year - Month**
      /\*\*Record:\s*([^-]+?)\s*-\s*(\d{4})\s*-\s*(\d+)\*\*/gi,
      // Dealer quoted format: dealer "DealerName"
      /dealer[^"]*"([^"]+)"/gi,
      // Simple dealer mention: The dealer DealerName
      /The dealer\s+([^,.\n]+)/gi
    ];
    
    for (const pattern of dealerPatterns) {
      const match = markdown.match(pattern);
      if (match) {
        const fullMatch = match[0];
        
        if (pattern.source.includes('\\|.*\\|.*\\|.*\\|')) {
          // Table format - extract dealer, year, month
          const tableMatch = fullMatch.match(/\|\s*([^|]+?)\s*\|\s*(\d{4})\s*\|\s*(\d+)\s*\|\s*\d+\s*\|/);
          if (tableMatch) {
            dealerName = tableMatch[1].trim();
            year = tableMatch[2];
            month = tableMatch[3];
          }
        } else if (pattern.source.includes('Record:')) {
          // Record format - extract dealer, year, month
          const recordMatch = fullMatch.match(/\*\*Record:\s*([^-]+?)\s*-\s*(\d{4})\s*-\s*(\d+)\*\*/);
          if (recordMatch) {
            dealerName = recordMatch[1].trim();
            year = recordMatch[2];
            month = recordMatch[3];
          }
        } else {
          // Other formats - extract just the dealer name
          const nameMatch = fullMatch.match(/["']([^"']+)["']|dealer[^"]*"([^"]+)"|The dealer\s+([^,.\n]+)/i);
          if (nameMatch) {
            dealerName = (nameMatch[1] || nameMatch[2] || nameMatch[3] || '').trim();
          }
        }
        
        // If we found a valid dealer name, break
        if (dealerName && dealerName !== 'Unknown Dealer' && !dealerName.match(/^\d+$/)) {
          break;
        }
      }
    }
    
    // If year and month weren't extracted from table/record patterns, try fallback extraction
    if (year === '2024' && month === '1') { // Default values, meaning we didn't extract them yet
      const yearMatches = markdown.match(/202[0-9]/g);
      if (yearMatches) {
        year = yearMatches[yearMatches.length - 1]; // Get the last year mentioned
      }
      
      const monthMatches = markdown.match(/\b(1[0-2]|[1-9])\b/g);
      if (monthMatches) {
        // Filter out scores and get actual month
        const possibleMonths = monthMatches.filter(m => parseInt(m) <= 12 && parseInt(m) !== finalScore);
        if (possibleMonths.length > 0) {
          month = possibleMonths[possibleMonths.length - 1];
        }
      }
    }
    
    dealers.push({
      dealer: dealerName,
      year: year,
      month: month,
      score: finalScore
    });
    
    console.log("Extracted dealer info:", { dealerName, year, month, score: finalScore });
  }
  
  return { dealers, hasScores: dealers.length > 0 };
};

/**
 * Create a clean KPI analysis result by extracting only the FINAL SUMMARY FOR DISPLAY section
 * @param markdown The original markdown from AI
 * @returns Only the final summary content
 */
const createCleanKPIAnalysis = (markdown: string): string => {
  if (!markdown) return '';
  
  console.log("Extracting FINAL SUMMARY - preserving AI's original formatting");
  console.log("Full markdown content preview:", markdown.substring(0, 500) + "...");
  console.log("Searching for FINAL SUMMARY FOR DISPLAY section...");
  
  // Simple search for the final summary section
  const finalSummaryIndex = markdown.search(/##?\s*FINAL\s+SUMMARY\s+FOR\s+DISPLAY/im);
  
  console.log("FINAL SUMMARY search result index:", finalSummaryIndex);
  
  if (finalSummaryIndex !== -1) {
    console.log("Found FINAL SUMMARY - extracting exactly as AI provided it");
    
    // Get everything after the header
    let finalContent = markdown.substring(finalSummaryIndex);
    
    // ONLY remove the header line - keep everything else exactly as the AI wrote it
    finalContent = finalContent.replace(/##?\s*FINAL\s+SUMMARY\s+FOR\s+DISPLAY\s*\n?/im, '');
    
    console.log("Preserved AI content length:", finalContent.length);
    
    return finalContent.trim();
  }
  
  console.log("FINAL SUMMARY FOR DISPLAY section not found, processing entire content for KPI highlighting");
  
  // If no final summary section found, return the entire content for KPI highlighting
  return markdown;
};



/**
 * Comprehensive markdown processing function that handles all markdown elements
 * @param markdown The markdown text to process
 * @param shouldHideCalculations Whether to hide calculation sections (default: true)
 * @param isKPIAnalysis Whether this is KPI analysis content that needs score extraction
 * @param kpiCodes Array of KPI codes to highlight in blue
 * @returns HTML string with all formatting applied
 */
/**
 * Simple KPI highlighting function
 */
const highlightKPIs = (text: string, kpiCodes: string[]): string => {
  if (!text || !kpiCodes || kpiCodes.length === 0) return text;
  
  let result = text;
  kpiCodes.forEach(kpi => {
    const regex = new RegExp(`\\b${kpi}\\b`, 'g');
    result = result.replace(regex, `<span style="color: #0066cc; font-weight: 600;">${kpi}</span>`);
  });
  
  return result;
};

export const processMarkdown = (markdown: string, shouldHideCalculations: boolean = true, isKPIAnalysis: boolean = false, kpiCodes: string[] = []): string => {
  if (!markdown) return '';
  
  // For KPI analysis, create minimal HTML directly
  if (isKPIAnalysis) {
    // Try to extract FINAL SUMMARY first, if not found process the whole content
    const cleanMarkdown = createCleanKPIAnalysis(markdown);
    console.log("KPI Analysis: Creating minimal HTML directly");
    console.log("Clean markdown preview:", cleanMarkdown.substring(0, 500));
    
    // Extract just the table and content we need
    const lines = cleanMarkdown.trim().split('\n').filter(line => line.trim() !== '');
    let html = '';
    let inTable = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Handle table rows
      if (line.startsWith('|') && line.endsWith('|')) {
        if (!inTable) {
          html += '<div class="table-responsive"><table class="markdown-table"><thead>';
          inTable = true;
          
          // Process header row
          const headers = line.split('|').filter(cell => cell.trim() !== '');
          html += '<tr>';
          headers.forEach(header => {
            html += `<th>${highlightKPIs(header.trim(), kpiCodes)}</th>`;
          });
          html += '</tr></thead><tbody>';
          
          // Skip separator row if next line contains dashes
          if (i + 1 < lines.length && lines[i + 1].includes('---')) {
            i++;
          }
        } else {
          // Process data row
          const cells = line.split('|').filter(cell => cell.trim() !== '');
          html += '<tr>';
          cells.forEach(cell => {
            html += `<td>${highlightKPIs(cell.trim(), kpiCodes)}</td>`;
          });
          html += '</tr>';
        }
      } else {
        // Close table if we were in one
        if (inTable) {
          html += '</tbody></table></div>';
          inTable = false;
        }
        
        // Handle other content - preserve AI's original formatting
        if (line.startsWith('###')) {
          html += `<h3>${highlightKPIs(line.replace('###', '').trim(), kpiCodes)}</h3>`;
        } else if (line.startsWith('##')) {
          html += `<h2>${highlightKPIs(line.replace('##', '').trim(), kpiCodes)}</h2>`;
        } else if (line.length > 0) {
          // Check if this line is just a KPI name that should be combined with the next line
          const trimmedLine = line.trim();
          const isJustKPIName = /^[A-Z_]+$/.test(trimmedLine) && trimmedLine.length > 2;
          
          if (isJustKPIName && i + 1 < lines.length && lines[i + 1].trim().length > 0) {
            // Combine KPI name with next line
            const nextLine = lines[i + 1].trim();
            const combinedLine = `${trimmedLine} ${nextLine}`;
            html += `<p>${highlightKPIs(combinedLine, kpiCodes)}</p>`;
            i++; // Skip the next line since we've combined it
          } else {
            // Just wrap everything else in paragraphs
            html += `<p>${highlightKPIs(line, kpiCodes)}</p>`;
          }
        }
      }
    }
    
    // Close table if still open
    if (inTable) {
      html += '</tbody></table></div>';
    }
    
    console.log("Created minimal HTML:", html.substring(0, 500));
    return html;
  }
  
  // For normal content, do full processing
  let processed = hideCalculationSections(markdown, shouldHideCalculations);
  
  // First process tables using the custom table processor
  processed = processMarkdownTables(processed);
  
  // Pre-process common date formats and headers with special characters to prevent them from being treated as raw markdown
  processed = processed
    // Handle year ranges with hyphens (like 2022-2023)
    .replace(/(\d{4})-(\d{4})/g, '<span class="date-range">$1-$2</span>')
    .replace(/(\d{4})-(\d{1,2})-(\d{1,2})/g, '<span class="date-format">$1-$2-$3</span>')
    .replace(/(^|\s)(\d{1,4}[-/.])(\d{1,2})([-/.]\d{1,4})?/gm, '$1<span class="date-format">$2$3$4</span>');
  
  // Then process other markdown elements
  processed = processed
    // Headers with styling - fix '#' characters in headers
    .replace(/^### (.*?)$/gim, '<h3 class="markdown-h3">$1</h3>')
    .replace(/^## (.*?)$/gim, '<h2 class="markdown-h2">$1</h2>')
    .replace(/^# (.*?)$/gim, '<h1 class="markdown-h1">$1</h1>')
    
    // Special handling for hash symbols that aren't meant to be headers
    .replace(/^([#]+)(?!\s)(.*?)$/gm, '<p class="markdown-paragraph"><span class="hash-symbol">$1</span>$2</p>')
    
    // Bold and italic
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    
    // Lists - handle with a comprehensive approach
    // First mark list items with temporary placeholders
    .replace(/^(\s*)-\s+(.*)/gm, '___UL_ITEM___$2')
    .replace(/^(\s*)\*\s+(.*)/gm, '___UL_ITEM___$2')
    .replace(/^(\s*)(\d+)\.\s+(.*)/gm, '___OL_ITEM_$2___$3')
    
    // Code blocks and inline code
    .replace(/```([\s\S]*?)```/g, '<pre class="markdown-pre"><code class="markdown-code">$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code class="markdown-inline-code">$1</code>')
    
    // Blockquotes
    .replace(/^>\s+(.*)/gm, '<blockquote class="markdown-blockquote">$1</blockquote>')
    
    // Paragraphs - only wrap text in p tags if it's not already wrapped in other tags
    .replace(/^(?!<[h|u|o|b|p|c|s])(.*[^\s].*)$/gim, '<p class="markdown-paragraph">$1</p>');
  
  // Restore date ranges that might have been parsed as headers
  processed = processed.replace(/<span class="date-range">(\d{4})-(\d{4})<\/span>/g, '$1-$2');
  processed = processed.replace(/<span class="date-format">(.*?)<\/span>/g, '$1');
  
  // Apply number formatting to the content
  processed = formatTextWithNumbers(processed);
  
  // Process list placeholders into proper HTML lists
  processed = processListPlaceholders(processed);
  
  // For KPI analysis, preserve the AI's formatting exactly as provided
  if (isKPIAnalysis) {
    console.log("KPI Analysis: Preserving AI's original formatting - no transformations applied");
  }
  
  return processed;
};

/**
 * Process list placeholders into proper HTML lists
 * @param text The text with list placeholders
 * @returns HTML string with proper list structures
 */
const processListPlaceholders = (text: string): string => {
  if (!text) return '';
  
  const lines = text.split('\n');
  const processedLines: string[] = [];
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i];
    
    // Handle unordered list items
    if (line.includes('___UL_ITEM___')) {
      const listItems: string[] = [];
      
      // Collect consecutive unordered list items
      while (i < lines.length && lines[i].includes('___UL_ITEM___')) {
        const content = lines[i].replace('___UL_ITEM___', '').trim();
        if (content) {
          listItems.push(`<li class="markdown-list-item">${content}</li>`);
        }
        i++;
      }
      
      if (listItems.length > 0) {
        processedLines.push(`<ul class="markdown-list">`);
        processedLines.push(...listItems);
        processedLines.push(`</ul>`);
      }
      continue;
    }
    
    // Handle ordered list items
    if (line.includes('___OL_ITEM_')) {
      const listItems: Array<{number: number, content: string}> = [];
      
      // Collect consecutive ordered list items
      while (i < lines.length && lines[i].includes('___OL_ITEM_')) {
        const match = lines[i].match(/___OL_ITEM_(\d+)___(.*)/);
        if (match) {
          const number = parseInt(match[1]);
          const content = match[2].trim();
          if (content) {
            listItems.push({ number, content });
          }
        }
        i++;
      }
      
      if (listItems.length > 0) {
        // Determine the starting number (use the first item's number)
        const startNumber = listItems[0].number;
        processedLines.push(`<ol class="markdown-list" start="${startNumber}">`);
        
        // Add all list items (they'll be numbered sequentially from the start number)
        listItems.forEach(item => {
          processedLines.push(`<li class="markdown-list-item">${item.content}</li>`);
        });
        
        processedLines.push(`</ol>`);
      }
      continue;
    }
    
    // Regular line - add as is
    processedLines.push(line);
    i++;
  }
  
  return processedLines.join('\n');
};

/**
 * Process markdown tables specifically
 * @param markdown The markdown text to process
 * @returns HTML string with tables processed
 */
export const processMarkdownTables = (markdown: string): string => {
  if (!markdown) return '';
  
  // Split content by lines
  const lines = markdown.split('\n');
  const processedLines = [];
  
  let inTable = false;
  let tableContent = '';
  let isFirstDataRow = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Detect if line is part of a table (has multiple | characters)
    const isTableRow = line.startsWith('|') && line.endsWith('|') && line.split('|').length > 2;
    
    if (isTableRow) {
      if (!inTable) {
        inTable = true;
        tableContent = '<div class="table-responsive"><table class="markdown-table"><thead>';
        
        // Process header row
        const headerCells = line.split('|').filter(cell => cell.trim() !== '');
        
        tableContent += '<tr>';
        headerCells.forEach((cell, index) => {
          const cellText = cell.trim();
          // Check if header contains year or date pattern
          if (containsDate(cellText)) {
            tableContent += `<th class="date-cell">${formatDateInHeader(cellText)}</th>`;
          } else {
            tableContent += `<th>${cellText}</th>`;
          }
        });
        tableContent += '</tr></thead><tbody>';
        
        // Skip the separator row (next line) if it exists
        if (i + 1 < lines.length && lines[i + 1].trim().includes('-')) {
          i++;
        }
        
        isFirstDataRow = true;
      } else {
        // Process data row
        const cells = line.split('|').filter(cell => cell.trim() !== '');
        tableContent += '<tr>';
        
        cells.forEach((cell, index) => {
          const cellText = cell.trim();
          
          // First column often contains row headers/identifiers
          if (index === 0) {
            tableContent += `<td class="row-header-cell">${cellText}</td>`;
          }
          // Explicitly check for year patterns to ensure they don't get formatted as numbers
          else if (/^\s*(20\d{2}|19\d{2})\s*$/.test(cellText)) {
            tableContent += `<td class="date-value-cell"><span class="year-value">${cellText}</span></td>`;
          }
          // Check for month-year patterns
          else if (/^\s*(January|February|March|April|May|June|July|August|September|October|November|December)\s+(20\d{2}|19\d{2})\s*$/i.test(cellText)) {
            const match = cellText.match(/^(.*?)\s+(20\d{2}|19\d{2})$/i);
            if (match) {
              tableContent += `<td class="date-value-cell"><span class="month-value">${match[1]}</span> <span class="year-value">${match[2]}</span></td>`;
            } else {
              tableContent += `<td class="date-value-cell">${cellText}</td>`;
            }
          }
          // Check if current cell contains another date format
          else if (containsDate(cellText)) {
            tableContent += `<td class="date-value-cell">${formatDateInCell(cellText)}</td>`;
          }
          // Otherwise, format normally
          else {
            tableContent += `<td>${formatCellContent(cellText)}</td>`;
          }
        });
        
        tableContent += '</tr>';
        isFirstDataRow = false;
      }
    } else if (inTable) {
      // End of table
      inTable = false;
      tableContent += '</tbody></table></div>';
      processedLines.push(tableContent);
    } else {
      processedLines.push(line);
    }
  }
  
  // If we're still in a table at the end, close it
  if (inTable) {
    tableContent += '</tbody></table></div>';
    processedLines.push(tableContent);
  }
  
  return processedLines.join('\n');
};

/**
 * Check if text contains a date or year pattern
 * @param text The text to check
 * @returns boolean
 */
export const containsDate = (text: string): boolean => {
  const plainText = text.replace(/<[^>]*>/g, '').trim();
  
  // Common date patterns to detect
  const patterns = [
    /\b(20\d{2})\b/, // Year like 2021
    /\b(19\d{2})\b/, // Year like 1999
    /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\b/i, // Month YYYY
    /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}\b/i, // Abbreviated month YYYY
    /\b\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{2,4}\b/, // DD/MM/YYYY or similar
    /\b\d{4}[\/\-.]\d{1,2}[\/\-.]\d{1,2}\b/, // YYYY/MM/DD or similar
    /\bQ[1-4]\s+\d{4}\b/i, // Q1 2021 etc.
    /\b\d{4}-\d{4}\b/, // Year range like 2021-2022
    /\bDecember\s+\d{4}\b/i // Match "December 2021" pattern specifically
  ];
  
  return patterns.some(pattern => pattern.test(plainText));
};

/**
 * Format date text in a header cell
 * @param text The header cell text
 * @returns Formatted HTML
 */
export const formatDateInHeader = (text: string): string => {
  // For headers containing dates, preserve the exact formatting
  // but add the date-header class to the content
  
  // If it's a simple year (2021, 2022, etc.)
  const yearMatch = text.match(/\b(20\d{2}|19\d{2})\b/);
  if (yearMatch) {
    return text.replace(yearMatch[0], `<span class="year-value">${yearMatch[0]}</span>`);
  }
  
  // If it's "December 2022" or similar month + year format
  const monthYearMatch = text.match(/\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(20\d{2}|19\d{2})\b/i);
  if (monthYearMatch) {
    return `<span class="month-value">${monthYearMatch[1]}</span> <span class="year-value">${monthYearMatch[2]}</span>`;
  }
  
  // For other date formats, just wrap the entire content
  return `<span class="date-header-value">${text}</span>`;
};

/**
 * Format a date string in a table cell
 * @param text The date text to format
 * @returns Formatted HTML string
 */
export const formatDateInCell = (text: string): string => {
  // Year pattern (2021, 2022, etc.)
  const yearMatch = text.match(/\b(20\d{2}|19\d{2})\b/);
  if (yearMatch) {
    return text.replace(yearMatch[0], `<span class="year-value">${yearMatch[0]}</span>`);
  }
  
  // Month-year pattern (December 2021, etc.)
  const monthYearMatch = text.match(/\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+(20\d{2}|19\d{2})\b/i);
  if (monthYearMatch) {
    const month = monthYearMatch[1];
    const year = monthYearMatch[2];
    return text.replace(monthYearMatch[0], `<span class="month-value">${month}</span> <span class="year-value">${year}</span>`);
  }
  
  // For other date formats, just wrap the entire text
  return `<span class="date-value">${text}</span>`;
};

/**
 * Format the content of a table cell, with special handling for certain patterns
 * @param text The cell text to format
 * @returns Formatted HTML string
 */
export const formatCellContent = (text: string): string => {
  // Handle special cases first
  
  // Strip any HTML tags for pattern matching
  const plainText = text.replace(/<[^>]*>/g, '').trim();
  
  // Check for year patterns first (like 2021, 2022, etc.)
  const yearMatch = plainText.match(/^\s*(20\d{2}|19\d{2})\s*$/);
  if (yearMatch) {
    return `<span class="year-value">${yearMatch[1]}</span>`;
  }
  
  // Check for month-year patterns (like "December 2021")
  const monthYearMatch = plainText.match(/^\s*(January|February|March|April|May|June|July|August|September|October|November|December)\s+(20\d{2}|19\d{2})\s*$/i);
  if (monthYearMatch) {
    return `<span class="month-value">${monthYearMatch[1]}</span> <span class="year-value">${monthYearMatch[2]}</span>`;
  }
  
  // Check for general date patterns
  if (containsDate(plainText)) {
    return `<span class="date-value">${text}</span>`;
  }
  
  // Currency values (€, $, £, ¥, etc.)
  if (/^[€$£¥]\s*[\d,.]+$|^[\d,.]+\s*[€$£¥]$|^[\d,.]+\s*(?:EUR|USD|GBP|JPY)$/.test(plainText)) {
    return `<span class="currency-value">${text}</span>`;
  }
  
  // Percentage values
  if (/^[\d,.]+%$/.test(plainText)) {
    return `<span class="percentage-value">${text}</span>`;
  }
  
  // Large numbers with optional decimals but no other characters
  // Exclude patterns that might be years
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$|^\d{4,}(\.\d+)?$/.test(plainText) && !yearMatch) {
    // Format with commas for thousands
    try {
      // Already formatted with commas
      if (plainText.includes(',')) {
        return `<span class="number-value">${text}</span>`;
      }
      
      const num = parseFloat(plainText);
      if (!isNaN(num)) {
        // Replace the number part while preserving any HTML
        const formattedNum = num.toLocaleString();
        return text.replace(plainText, `<span class="number-value">${formattedNum}</span>`);
      }
    } catch (e) {
      // If parsing fails, return unchanged
    }
  }
  
  // For all other text, just return as is
  return text;
};